import { createContext, useContext, useMemo, type ReactNode } from 'react';
import {
  dateTime as dateTimeBase,
  money as moneyBase,
  relative as relativeBase,
} from '@wyocspay/ui';

/**
 * Dates and times in the merchant's own convention.
 *
 * `07/08/2026` is the eighth of July to an American and the seventh of August
 * to everyone else. On a page listing when money moved, that is not a
 * presentational detail - a merchant reading a payout date a month out is a
 * support ticket at best.
 *
 * The locale is derived from the country the merchant registered the business
 * in, not from the browser: the business's own convention is what its
 * bookkeeping uses, and the same account read from a laptop abroad must not
 * change what a date means. The interface language is a separate choice and
 * does not move this.
 */

/**
 * Country to locale.
 *
 * Only where the country genuinely picks a convention. Anything unlisted falls
 * back to `en-GB`, which is day-month like most of the world - a safer default
 * than `en-US` for an unknown country, since month-first is the minority
 * convention and the one that misreads.
 */
const COUNTRY_LOCALE: Record<string, string> = {
  US: 'en-US',
  GB: 'en-GB',
  BG: 'bg-BG',
  DE: 'de-DE',
  AT: 'de-AT',
  CH: 'de-CH',
  FR: 'fr-FR',
  BE: 'nl-BE',
  NL: 'nl-NL',
  IT: 'it-IT',
  ES: 'es-ES',
  PT: 'pt-PT',
  PL: 'pl-PL',
  CZ: 'cs-CZ',
  SK: 'sk-SK',
  HU: 'hu-HU',
  RO: 'ro-RO',
  GR: 'el-GR',
  HR: 'hr-HR',
  SI: 'sl-SI',
  SE: 'sv-SE',
  NO: 'nb-NO',
  DK: 'da-DK',
  FI: 'fi-FI',
  EE: 'et-EE',
  LV: 'lv-LV',
  LT: 'lt-LT',
  IE: 'en-IE',
  CA: 'en-CA',
  AU: 'en-AU',
  NZ: 'en-NZ',
  JP: 'ja-JP',
  SG: 'en-SG',
  HK: 'en-HK',
  MX: 'es-MX',
  BR: 'pt-BR',
  CY: 'el-CY',
  MT: 'en-MT',
  LU: 'fr-LU',
  GI: 'en-GB',
  LI: 'de-LI',
};

export function localeForCountry(country: string | null | undefined): string {
  if (!country) return 'en-GB';
  return COUNTRY_LOCALE[country.toUpperCase()] ?? 'en-GB';
}

interface LocaleValue {
  locale: string;
  country: string;
  /** What the connected account settles in, per Stripe. */
  settlementCurrency: string | null;
  dateTime: (iso: string | null) => string;
  relative: (iso: string | null) => string;
  money: (amount: number, currency: string) => string;
  /** Long form, for invoices and documents. */
  dateLong: (iso: string | null) => string;
}

const LocaleContext = createContext<LocaleValue>({
  locale: 'en-GB',
  country: 'GB',
  settlementCurrency: null,
  dateTime: (iso) => dateTimeBase(iso, 'en-GB'),
  relative: (iso) => relativeBase(iso, 'en-GB'),
  money: (a, c) => moneyBase(a, c, 'en-GB'),
  dateLong: (iso) => (iso ? new Date(iso).toLocaleDateString('en-GB', { dateStyle: 'long' }) : '-'),
});

export function LocaleProvider({
  country,
  settlementCurrency,
  children,
}: {
  country: string | null | undefined;
  settlementCurrency?: string | null;
  children: ReactNode;
}) {
  const value = useMemo<LocaleValue>(() => {
    const locale = localeForCountry(country);
    return {
      locale,
      country: (country ?? 'GB').toUpperCase(),
      settlementCurrency: settlementCurrency ?? null,
      dateTime: (iso) => dateTimeBase(iso, locale),
      relative: (iso) => relativeBase(iso, locale),
      money: (amount, currency) => moneyBase(amount, currency, locale),
      dateLong: (iso) =>
        iso ? new Date(iso).toLocaleDateString(locale, { dateStyle: 'long' }) : '-',
    };
  }, [country, settlementCurrency]);

  return <LocaleContext.Provider value={value}>{children}</LocaleContext.Provider>;
}

export function useLocale(): LocaleValue {
  return useContext(LocaleContext);
}
